import React, { useMemo } from "react";
import { DollarSign, Rocket, Target, Calendar, Edit, Package } from "lucide-react";
import {
  calculateAssetBookValueAtDate,
  resolveActiveAssetEvents,
} from "@burnchart/financial";
import type { LucideIcon } from "lucide-react";
import { DynamicIcon } from "../../atoms";
import type { EventData } from "../../../types/burndown.types";
import type { EventsTimelineProps } from "./EventsTimeline.types";

const RECENT_EVENT_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function compareEventsForDisplay(a: EventData, b: EventData): number {
  if (a.type === "asset" && b.type !== "asset") return -1;
  if (a.type !== "asset" && b.type === "asset") return 1;
  if (a.description !== b.description) {
    return a.description.localeCompare(b.description);
  }
  return b.date.getTime() - a.date.getTime();
}

export const EventsTimeline: React.FC<EventsTimelineProps> = ({
  currentDate,
  data,
  onEventClick,
}) => {
  const currentEvents = useMemo(() => {
    const activeAssets = resolveActiveAssetEvents(data.events, currentDate);

    const recentNonAssetEvents = data.events.filter((event) => {
      if (event.type === "asset") return false;
      if (event.date.getTime() > currentDate.getTime()) return false;
      return (
        event.date.getTime() >= currentDate.getTime() - RECENT_EVENT_WINDOW_MS
      );
    });

    return [...activeAssets, ...recentNonAssetEvents].sort(compareEventsForDisplay);
  }, [currentDate, data.events]);

  const assetCount = currentEvents.filter((event) => event.type === "asset").length;

  return (
    <div className="border-b border-white/10 bg-surface-secondary/50 p-4">
      <p className="sh-kicker mb-1">Timeline position</p>
      <h4 className="mb-1 font-semibold text-white">
        {currentDate.toLocaleDateString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        })}
      </h4>
      <p className="mb-3 text-xs text-white/45">
        {assetCount > 0
          ? `${assetCount} active asset${assetCount === 1 ? "" : "s"}`
          : "No active assets"}
        {" · "}
        other events from the last 30 days
      </p>

      {currentEvents.length === 0 ? (
        <div className="text-sm text-white/45">
          No assets or recent events at this point on the timeline.
        </div>
      ) : (
        <div className="space-y-2">
          {currentEvents.map((event) => {
            const priorityColors = {
              high: "border-red-400 bg-danger/10 text-rose-200",
              medium: "border-warn/50 bg-warn/10 text-warn",
              low: "border-green-400 bg-brand/10 text-emerald-200",
            };

            const defaultIcons: Record<string, LucideIcon> = {
              funding: DollarSign,
              launch: Rocket,
              milestone: Target,
              asset: Package,
              custom: Calendar,
            };

            const defaultIcon = defaultIcons[event.type] || Calendar;
            const isAsset = event.type === "asset";
            const isRevision = event.segmentKind === "revision";

            return (
              <div
                key={event.id}
                className={`rounded-lg border-l-4 p-3 transition-all duration-200 ${
                  priorityColors[event.priority]
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <DynamicIcon
                        iconName={event.icon}
                        fallbackIcon={defaultIcon}
                        className="h-5 w-5"
                        color={event.color}
                      />
                      <span className="text-sm font-medium capitalize">
                        {isAsset ? "asset" : event.type}
                        {isAsset ? (
                          <>
                            {" "}
                            · since {event.date.toLocaleDateString()}
                            {isRevision ? (
                              <span className="ml-1.5 rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-200">
                                revised
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <> · {event.date.toLocaleDateString()}</>
                        )}
                      </span>
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          event.priority === "high"
                            ? "bg-danger/15 text-danger"
                            : event.priority === "medium"
                              ? "bg-yellow-100 text-warn"
                              : "bg-brand/15 text-brand"
                        }`}
                      >
                        {event.priority}
                      </span>
                      <button
                        onClick={() => onEventClick?.(event.id)}
                        className="ml-auto rounded p-1 text-white/40 transition-colors hover:bg-white/[0.06] hover:text-white/65"
                        title="Edit event"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-sm text-white/75">{event.description}</p>
                    {isAsset && event.revisionNote ? (
                      <p className="mt-1 text-xs text-white/45">
                        {event.revisionNote}
                      </p>
                    ) : null}
                  </div>
                  {event.amount != null && event.amount > 0 && (
                    <div className="ml-4 text-right">
                      <div className="text-lg font-bold text-brand">
                        {isAsset ? (
                          <>
                            $
                            {calculateAssetBookValueAtDate(
                              event,
                              currentDate,
                            ).toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                          </>
                        ) : (
                          <>+${event.amount.toLocaleString()}</>
                        )}
                      </div>
                      <div className="text-xs text-white/45">
                        {isAsset ? "book value" : "funding"}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
